import { CBSocketClient, SocketClient } from "cbcore-ts"
import { IF, NO, YES } from "uicore-ts"
import {
    RHProcurement,
    RHProcurementCopySource,
    RHProcurementPublic,
    RHProcurementStatus,
    RHPublishedProcurementStatus,
    RHReconciliationCandidateCompany
} from "../../../models/prodModels"
import type { RHPublishedProcurementsQuery } from "../../../models/PublishedProcurementSearch"
import type {
    RHAmendPublishedProcurementInput,
    RHInviteCompaniesToProcurementInput,
    RHProcurementInvitationResult,
    RHAddOrModifyNewProcurementResult,
    RHCompanyAffiliation,
    RHCPVSummationReport,
    RHCPVSummationReportRequest,
    RHConflictOfInterestReasonCode,
    RHConflictOfInterestResult,
    InvitedCompany,
    RHFileMetadata,
    RHOfferCapabilities,
    RHOfferCreationCapabilities,
    RHOffererDashboardProcurement,
    RHOffererSubmissionOutcome,
    RHProcurementAmendmentPreview,
    RHProcurementCapabilities,
    RHProcurementCopyListRow,
    RHProcurementDashboardEnvelope,
    RHProcurementEngagementSummary,
    RHProcurementExcelExport,
    RHProcurementOpeningProtocolSharing,
    RHProcurementInformativeStatus,
    RHProcurementLifecycleState,
    RHProcurementPublicationVisibility,
    RHPublishProcurementResult,
    RHProcurementOfferStateSnapshot,
    RHProcurementTeam,
    RHProcurementTeamRole,
    RHProcurementSourceSelectionMode,
    RHProcurementSourceSelectionResult,
    RHProcurementTemplateForUseValue,
    RHProcurementTemplateInvitationImportResult,
    RHProcurementTemplateInvitationImportRow,
    RHProcurementTemplateLinkValue,
    RHProcurementTemplateMutationValue,
    RHProcurementTemplateResult,
    RHProcurementTemplateUsageXLSXResult,
    RHProcurementTendererParticipation,
    RHProcurerDashboardProcurement,
    RHPublishedProcurementRow,
    RHPublishedProcurementListRow,
    RHPublishedProcurementsPage,
    RHReconciliationRoundSummary,
    RHResolvedConflictEntry,
    RHUnresolvedConflictEntry,
    RHContractingAuthorityType,
    RHRegistryProcurement
} from "../SocketClientFunctions"
import { companyIdForAffiliation } from "./RHCompanyAffiliationStore"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"
import type { RHFilePublic } from "../../../models/prodModels"
import type { MongooseReference } from "../../../mongooseUtil"
import { populatedReferenceOf, populatedValueOfReference, referenceId } from "./RHReference"


// ============================================================================
// Push message payload — shared shape for both push message keys
// ============================================================================

export interface RHProcurementChangePayload {
    changedIds: string[]
}

// Payload pushed to every registered tenderer's active sessions when a
// procurer changes the deadline of a published procurement.
export interface RHProcurementDeadlineChangedPayload {
    procurementId: string
    procurementTitle: string
    newDeadlineDate: string        // ISO string
    newDeadlineFormatted: string   // Human-readable, e.g. "01.06.2025 14:00"
    wasAutoExtended?: boolean
}

export interface RHProcurementAmendedPayload {
    procurementId: string
    procurementTitle: string
}


// Clean result type returned by retrieveProcurementInformation.
// File references are hydrated when metadata access is available. The primary
// procurement result remains usable even when optional file enrichment fails.
export interface RHProcurementInformationResult {
    procurement: RHProcurement & RHProcurementPublic
    openingProtocolSharing?: RHProcurementOpeningProtocolSharing
    isCurrentUserOnProcuringSide: boolean
    userOffers: RHProcurementOfferStateSnapshot[]
    offerCapabilities?: RHOfferCapabilities
    offerCreationCapabilities?: RHOfferCreationCapabilities
    canWithdrawTendererRegistration: boolean
    unseenQANotificationCount: number
    unseenOfferNotificationCount: number
    unseenProcurementNotificationCount: number
    notificationSnapshotAt: string
    requiresProcurerAttention: boolean
    requiresProcurerQAAttention: boolean
    capabilities?: RHProcurementCapabilities
    engagementSummary?: RHProcurementEngagementSummary
    offerCount: number
    activePartOfferCount: number
    lifecycleState?: RHProcurementLifecycleState
    isClosedWithoutContractInReconciliationChain: boolean
    procurerInformativeStatus?: RHProcurementInformativeStatus
    offererInformativeStatus?: RHProcurementInformativeStatus
    offererSubmissionOutcome?: RHOffererSubmissionOutcome
    notificationStateGeneration: number
}


// ============================================================================
// RHProcurementStore
//
// Centralises all procurement reads and writes. Views never call SocketClient
// for procurement operations directly — they go through this store instead.
//
// Subscription system inherited from RHSubscriptionBase:
//   store.subscribe(this, changedIds => { ... })
//   store.unsubscribe(this)
//
// Subscribe in viewWillAppear BEFORE the first data read.
// Unsubscribe in viewWillDisappear.
//
// Server push:
//   "OwnProcurementsDidChange"      — sent to procuring-company members
//   "PublishedProcurementsDidChange" — broadcast to all authenticated sessions
// Both carry { changedIds: string[] }.
// ============================================================================

// What the client attaches to a procurement it holds: the engagement summary sent beside it, and its unseen notification count.
type RHProcurementClientExtras = {
    engagementSummary?: RHProcurementEngagementSummary
    unseenNotificationCount?: number
}


export class RHProcurementStore extends RHSubscriptionBase<(changedIds: string[]) => void> {

    // Inline caches — populated by writes, cleared by push messages.
    // No TTL: reads always go to the server unless a write just populated the
    // cache in the same session, in which case the fresh value is already there.
    _ownProcurementsByStatusKey = new Map<string, RHProcurementDashboardEnvelope>()
    _ownProcurementsAcrossAllCompaniesByStatusKey = new Map<string, RHProcurerDashboardProcurement[]>()
    _publishedProcurementsByStatusKey = new Map<string, RHPublishedProcurementRow[]>()
    // The procurer names depend on the viewer and on what is published, not on the search, so they are held per status set.
    private _publishedProcurerNamesByStatusKey = new Map<string, Promise<string[]>>()
    private _procurementById = new Map<string, RHProcurement & RHProcurementClientExtras>()
    // One object per procurement the search table has shown: later loads of the rest of its data fill this same object in.
    private _listRowById = new Map<string, RHPublishedProcurementListRow>()
    _procurementSourceListsByCompanyAndMode =
        new Map<string, RHProcurementCopyListRow[]>()
    _copySourceByCompanyAndId = new Map<string, RHProcurementCopySource>()
    _templateUseByCompanyTemplateAndVersion =
        new Map<string, RHProcurementTemplateForUseValue>()
    _cpvSummationRequestsByKey = new Map<string, Promise<RHCPVSummationReport | undefined>>()
    _cpvSummationGeneration = 0


    constructor(private _core: RHCore) {
        super()
        // Registered on the current socket client AND re-registered on every
        // socket client replacement (login/logout reconnect) — otherwise the
        // store would stop receiving pushes after an in-page login. The own-list
        // cache is user-specific, so it must not survive that session boundary.
        this._registerSocketListeners()
        this._core._functionsToCallForEachSocketClient.push(() => {
            this._clearOwnProcurementsCaches()
            this._registerSocketListeners()
        })
    }


    // Clears both own-procurement caches (single active company, and the
    // cross-company fan-out) together — they must always invalidate in
    // lockstep, since either one going stale would show a procurement that no
    // longer matches the other's view.
    private _clearPublishedProcurementsCaches() {
        this._publishedProcurementsByStatusKey.clear()
        this._publishedProcurerNamesByStatusKey.clear()
    }


    _clearOwnProcurementsCaches() {
        this._ownProcurementsByStatusKey.clear()
        this._ownProcurementsAcrossAllCompaniesByStatusKey.clear()
        this._cpvSummationRequestsByKey.clear()
        this._cpvSummationGeneration += 1
    }


    /** Resets all cached procurements. Call on logout so the next login never sees a stale user's data. */
    clear() {
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._procurementById.clear()
        this._listRowById.clear()
        this._invalidateCopyProcurementCaches()
        this._notifySubscribers([])
    }


    // =========================================================================
    // Read API
    // =========================================================================

    async retrieveCPVSummationReportForRequest(request: RHCPVSummationReportRequest): Promise<RHCPVSummationReport | undefined> {
        const companyId = this._core.companyAffiliationStore.activeCompanyId
        if (!companyId || request.buyerId !== companyId) {
            return undefined
        }
        const requestKey = JSON.stringify(request)
        const pending = this._cpvSummationRequestsByKey.get(requestKey)
        if (pending) {
            return pending
        }
        const generation = this._cpvSummationGeneration
        const promise = SocketClient.PreviewCPVSummation(request, CBSocketClient.completionPolicy.first).then(response => {
            if (generation !== this._cpvSummationGeneration || companyId !== this._core.companyAffiliationStore.activeCompanyId) {
                return undefined
            }
            if (!response.result) {
                const fallback = "CPV summeerimise laadimine ebaõnnestus."
                throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
            }
            return response.result
        })
        this._cpvSummationRequestsByKey.set(requestKey, promise)
        try {
            return await promise
        }
        finally {
            // Share only in-flight reads; reopening a form must include newly signed contracts and the current day.
            if (this._cpvSummationRequestsByKey.get(requestKey) === promise) {
                this._cpvSummationRequestsByKey.delete(requestKey)
            }
        }
    }


    async retrieveCPVSummationReportForProcurement(procurementId: string): Promise<RHCPVSummationReport> {
        const response = await SocketClient.ValidateCPVSummationBeforePublish(
            { procurementId }, CBSocketClient.completionPolicy.first
        )
        if (!response.result) {
            const fallback = "CPV summeerimise kontroll ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        return response.result
    }


    /** With shouldRequireTeamMembership, selects the current user's team roles; the default preserves draft/template access. */
    async retrieveOwnProcurementDashboard(
        statuses: RHProcurementStatus[],
        shouldRequireTeamMembership: boolean = NO
    ): Promise<RHProcurementDashboardEnvelope> {
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const cacheKey = RHProcurementStore._companyModeCacheKey(
            activeCompanyId,
            `${shouldRequireTeamMembership}:${RHProcurementStore._statusCacheKey(statuses)}`
        )
        const cached = this._ownProcurementsByStatusKey.get(cacheKey)
        if (cached) {
            return cached
        }
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        let data: RHProcurementDashboardEnvelope
        if (activeCompanyId) {
            data = (await SocketClient.RetrieveOwnOngoingProcurementsForCompany(
                { companyId: activeCompanyId, statuses, shouldRequireTeamMembership },
                CBSocketClient.completionPolicy.first
            )).result
        }
        else {
            data = (await SocketClient.RetrieveOwnOngoingProcurementsListForCurrentProcurerUser(
                { statuses, shouldRequireTeamMembership },
                CBSocketClient.completionPolicy.first
            )).result
        }
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.retrieveOwnProcurementDashboard(statuses, shouldRequireTeamMembership)
        }
        this._ownProcurementsByStatusKey.set(cacheKey, data)
        this._feedNotificationCounts(RHProcurementStore._withReconciliationRoundsFlattened(data.rows))
        return data
    }


    async retrieveOwnProcurements(
        statuses: RHProcurementStatus[]
    ): Promise<RHProcurerDashboardProcurement[]> {
        return (await this.retrieveOwnProcurementDashboard(statuses, YES)).rows
    }


    /**
     * Own procurements across every company the user currently belongs to, not
     * just the active one. Never adds a new server-side aggregation endpoint —
     * it fans out the existing per-company `RetrieveOwnOngoingProcurementsForCompany`
     * once per company the server already vouches for via `RetrieveMyCompanyAffiliations`,
     * then merges client-side. Each company's own authorization check therefore
     * still runs exactly as it does for the single-company view.
     */
    async retrieveOwnProcurementsAcrossAllMyCompanies(
        statuses: RHProcurementStatus[],
        shouldRequireTeamMembership: boolean = NO
    ): Promise<RHProcurerDashboardProcurement[]> {
        const cacheKey = `${shouldRequireTeamMembership}:${RHProcurementStore._statusCacheKey(statuses)}`
        const cached = this._ownProcurementsAcrossAllCompaniesByStatusKey.get(cacheKey)
        if (cached) {
            return cached
        }

        const activeCompanyId = this._core.companyAffiliationStore.activeCompanyId
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const affiliations = await this._core.companyAffiliationStore.retrieveMyCompanyAffiliations()
        const eligibleCompanyIds = affiliations
            .filter(affiliation => RHProcurementStore._affiliationHasOwnProcurementListAccess(affiliation))
            .map(affiliation => companyIdForAffiliation(affiliation))
            .filter((companyId): companyId is string => Boolean(companyId))

        const resultsByCompany = await Promise.all(
            eligibleCompanyIds.map(companyId =>
                SocketClient.RetrieveOwnOngoingProcurementsForCompany(
                    { companyId, statuses, shouldRequireTeamMembership },
                    CBSocketClient.completionPolicy.first
                ).then(response => response.result.rows)
            )
        )

        const data = resultsByCompany
            .flat()
            .sort((first, second) =>
                RHProcurementStore._ownOngoingProcurementSortValue(second)
                    .localeCompare(RHProcurementStore._ownOngoingProcurementSortValue(first))
            )

        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            if (activeCompanyId !== this._core.companyAffiliationStore.activeCompanyId) {
                return data
            }
            return this.retrieveOwnProcurementsAcrossAllMyCompanies(statuses, shouldRequireTeamMembership)
        }

        this._ownProcurementsAcrossAllCompaniesByStatusKey.set(cacheKey, data)
        return data
    }


    async retrievePublishedProcurements(statuses: RHPublishedProcurementStatus[]): Promise<RHPublishedProcurementRow[]> {
        const cacheKey = RHProcurementStore._statusCacheKey(statuses)
        const cached = this._publishedProcurementsByStatusKey.get(cacheKey)
        if (cached) {
            return cached
        }
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const data = (await SocketClient.RetrievePublishedProcurements(
                { statuses },
                CBSocketClient.completionPolicy.first
            )
        ).result
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.retrievePublishedProcurements(statuses)
        }
        this._publishedProcurementsByStatusKey.set(cacheKey, data)
        return data
    }


    /** Procurer name suggestions; the optional company scope also includes own-company drafts. */
    retrievePublishedProcurerNames(
        statuses: RHPublishedProcurementStatus[],
        shouldIncludeOwnCompanyProcurements: boolean = NO
    ): Promise<string[]> {
        const cacheKey = `${shouldIncludeOwnCompanyProcurements}:${RHProcurementStore._statusCacheKey(statuses)}`
        const cached = this._publishedProcurerNamesByStatusKey.get(cacheKey)
        if (cached) {
            return cached
        }
        const request = SocketClient.RetrievePublishedProcurerNames(
            { statuses, shouldIncludeOwnCompanyProcurements }, CBSocketClient.completionPolicy.first
        )
            .then(response => response.result)
        this._publishedProcurerNamesByStatusKey.set(cacheKey, request)
        request.catch(() => {
            if (this._publishedProcurerNamesByStatusKey.get(cacheKey) === request) {
                this._publishedProcurerNamesByStatusKey.delete(cacheKey)
            }
        })
        return request
    }


    /**
     * One page of the published procurements matching the query, filtered, sorted and counted on the
     * server. Pages are not cached here: the caller owns the rows it has loaded and reloads them
     * when the store notifies it of a change.
     */
    async retrievePublishedProcurementsPage(
        request: {
            statuses: RHPublishedProcurementStatus[]
            shouldIncludeOwnCompanyProcurements?: boolean
            query: RHPublishedProcurementsQuery
            offset: number
            limit: number
        }
    ): Promise<RHPublishedProcurementsPage> {
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const page = (await SocketClient.RetrievePublishedProcurementsPage(
                request,
                CBSocketClient.completionPolicy.first
            )
        ).result
        // The rows carry unseen-notification counts, so a page read across a notification change is stale.
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.retrievePublishedProcurementsPage(request)
        }
        return { ...page, rows: page.rows.map(row => this._rememberListRow(row)) }
    }


    /**
     * The store's single object for the row's procurement: a row that arrives for a procurement already
     * held in full only refreshes the per-viewer fields, so it never replaces the full parts with the table's few.
     */
    private _rememberListRow(row: RHPublishedProcurementListRow): RHPublishedProcurementListRow {
        const id = row._id!.toString()
        const completeProcurement = this._procurementById.get(id)
        if (completeProcurement) {
            const refreshedProcurement = Object.assign(completeProcurement, RHProcurementStore._viewerFieldsOfListRow(row))
            // A full procurement whose company arrived as a bare reference cannot show its name, so the row stays separate.
            if (RHProcurementStore._isListRow(refreshedProcurement)) {
                this._listRowById.set(id, refreshedProcurement)
                return refreshedProcurement
            }
        }
        const knownRow = this._listRowById.get(id)
        if (!knownRow) {
            this._listRowById.set(id, row)
            return row
        }
        for (const fieldName of RHProcurementStore._listRowFieldNames) {
            if (!(fieldName in row)) {
                Reflect.deleteProperty(knownRow, fieldName)
            }
        }
        return Object.assign(knownRow, row)
    }


    /**
     * Keeps the full procurement in the same object the search table holds for it, so rows already on screen
     * receive the extra data without being looked up again. Fields the full data lacks are dropped, except the per-viewer ones.
     */
    private _rememberCompleteProcurement<Procurement extends RHProcurement & RHProcurementClientExtras>(
        id: string,
        procurement: Procurement
    ): Procurement {
        const knownRow = this._listRowById.get(id)
        if (!knownRow || Object.is(knownRow, procurement)) {
            this._procurementById.set(id, procurement)
            return procurement
        }
        for (const fieldName of Object.keys(knownRow)) {
            if (!(fieldName in procurement) && !RHProcurementStore._viewerFieldNames.some(name => name === fieldName)) {
                Reflect.deleteProperty(knownRow, fieldName)
            }
        }
        const procuringCompanyWithName = knownRow.procuringCompany
        const mergedProcurement = Object.assign(knownRow, procurement)
        // The table reads the company's name, which a bare reference in the full data does not carry.
        if (!RHProcurementStore._isListRow(mergedProcurement)) {
            Reflect.set(mergedProcurement, "procuringCompany", procuringCompanyWithName)
        }
        this._procurementById.set(id, mergedProcurement)
        return mergedProcurement
    }


    private static readonly _viewerFieldNames = [
        "unseenNotificationCount",
        "requiresOffererAttention",
        "hasSubmittedOfferForCurrentCompany",
        "hasViewableOfferDraftForCurrentCompany",
        "offererSubmissionOutcome",
        "lifecycleState",
        "offererInformativeStatus"
    ] satisfies (keyof RHPublishedProcurementListRow)[]


    private static readonly _listRowFieldNames = [
        "_id",
        "referenceNumber",
        "title",
        "status",
        "procuringCompany",
        "publishEstimatedCostOfProcurement",
        "deadlineDate",
        "publishedAt",
        "parts",
        ...RHProcurementStore._viewerFieldNames
    ] satisfies (keyof RHPublishedProcurementListRow)[]


    private static _isListRow<Candidate extends object>(candidate: Candidate): candidate is Candidate & RHPublishedProcurementListRow {
        return "procuringCompany" in candidate && typeof candidate.procuringCompany === "object" &&
            candidate.procuringCompany !== null && "name" in candidate.procuringCompany
    }


    private static _viewerFieldsOfListRow(row: RHPublishedProcurementListRow) {
        return {
            unseenNotificationCount: row.unseenNotificationCount,
            requiresOffererAttention: row.requiresOffererAttention,
            hasSubmittedOfferForCurrentCompany: row.hasSubmittedOfferForCurrentCompany,
            hasViewableOfferDraftForCurrentCompany: row.hasViewableOfferDraftForCurrentCompany,
            offererSubmissionOutcome: row.offererSubmissionOutcome,
            lifecycleState: row.lifecycleState,
            offererInformativeStatus: row.offererInformativeStatus
        }
    }


    /** The full reconciliation round chain a procurement belongs to — powers the details page's round switcher. */
    async retrieveReconciliationRoundChain(procurementId: string): Promise<RHReconciliationRoundSummary[]> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveReconciliationRoundChain(
            { procurementId, offererCompanyId },
            CBSocketClient.completionPolicy.first
        )
        return response.result.chain
    }


    async getOffererDashboardProcurements(): Promise<RHOffererDashboardProcurement[]> {
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const data = (await SocketClient.RetrieveOffererDashboardProcurements(
                { companyId: activeCompanyId },
                CBSocketClient.completionPolicy.first
            )
        ).result
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.getOffererDashboardProcurements()
        }
        this._feedNotificationCounts(RHProcurementStore._withReconciliationRoundsFlattened(data))
        return data
    }


    // Offerer-side counterpart of retrieveOwnProcurementsAcrossAllMyCompanies; no client fan-out needed here.
    async retrieveOffererDashboardProcurementsAcrossAllMyCompanies(): Promise<RHOffererDashboardProcurement[]> {
        const activeCompanyId = this._core.companyAffiliationStore.activeCompanyId
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const data = (await SocketClient.RetrieveOffererDashboardProcurements(
                {},
                CBSocketClient.completionPolicy.first
            )
        ).result
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            if (activeCompanyId !== this._core.companyAffiliationStore.activeCompanyId) {
                return data
            }
            return this.retrieveOffererDashboardProcurementsAcrossAllMyCompanies()
        }
        return data
    }


    async getProcurement(id: string): Promise<RHProcurement> {
        const cached = this._procurementById.get(id)
        if (cached) {
            return cached
        }
        return this.refreshProcurement(id)
    }


    /**
     * Retrieves the authoritative full procurement even when an older
     * detail snapshot exists in memory. Mutation forms use this before they
     * capture publishedVersion/updatedAt for optimistic concurrency checks.
     */
    async refreshProcurement(id: string): Promise<RHProcurement> {
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const response = await SocketClient.RetrieveProcurementPrivateInformation(
            { id },
            CBSocketClient.completionPolicy.first
        )
        const data = response.result
        if (!data) {
            const fallback = "Hanke andmete laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.refreshProcurement(id)
        }
        const procurement = this._rememberCompleteProcurement(id, data)
        this._feedNotificationCounts([procurement])
        return procurement
    }


    async retrieveProcurementSources(
        mode: RHProcurementSourceSelectionMode,
        companyId?: string
    ): Promise<RHProcurementCopyListRow[]> {
        const activeCompanyId = companyId ?? await this._activeCompanyIdForExplicitCompanyCalls()
        const cacheKey = RHProcurementStore._companyModeCacheKey(activeCompanyId, mode)
        const cached = this._procurementSourceListsByCompanyAndMode.get(cacheKey)
        if (cached) {
            return cached
        }
        let data: RHProcurementCopyListRow[]
        if (activeCompanyId) {
            data = (await SocketClient.RetrieveCopyableProcurementsForCompany(
                { companyId: activeCompanyId, mode },
                CBSocketClient.completionPolicy.first
            )).result
        }
        else {
            data = (await SocketClient.RetrieveCopyableProcurementsForCurrentProcurerUser(
                { mode },
                CBSocketClient.completionPolicy.first
            )).result
        }
        this._procurementSourceListsByCompanyAndMode.set(cacheKey, data)
        return data
    }


    async retrieveCopyableProcurements(): Promise<RHProcurementCopyListRow[]> {
        return this.retrieveProcurementSources("previousProcurement")
    }


    async retrieveProcurementCopySource(
        id: string,
        companyId?: string
    ): Promise<RHProcurementCopySource> {
        const activeCompanyId = companyId ?? await this._activeCompanyIdForExplicitCompanyCalls()
        const cacheKey = RHProcurementStore._companyModeCacheKey(activeCompanyId, id)
        const cached = this._copySourceByCompanyAndId.get(cacheKey)
        if (cached) {
            return cached
        }
        let data: RHProcurementCopySource
        if (activeCompanyId) {
            data = (await SocketClient.RetrieveProcurementCopySourceForCompany(
                { companyId: activeCompanyId, id },
                CBSocketClient.completionPolicy.first
            )).result
        }
        else {
            data = (await SocketClient.RetrieveProcurementCopySourceForCurrentProcurerUser(
                { id },
                CBSocketClient.completionPolicy.first
            )).result
        }
        this._copySourceByCompanyAndId.set(cacheKey, data)
        return data
    }


    async retrieveProcurementTemplateForUse(
        companyId: string,
        templateId: string,
        expectedTemplateUpdatedAt?: string
    ): Promise<RHProcurementTemplateResult<RHProcurementTemplateForUseValue>> {
        if (expectedTemplateUpdatedAt) {
            const cached = this._templateUseByCompanyTemplateAndVersion.get(
                RHProcurementStore._templateUseCacheKey(
                    companyId,
                    templateId,
                    expectedTemplateUpdatedAt
                )
            )
            if (cached) {
                return { success: YES, value: cached }
            }
        }
        const response = await SocketClient.RetrieveProcurementTemplateForUse(
            { companyId, templateId },
            CBSocketClient.completionPolicy.first
        )
        const result = response.result
        if (result.success) {
            this._templateUseByCompanyTemplateAndVersion.set(
                RHProcurementStore._templateUseCacheKey(
                    companyId,
                    templateId,
                    result.value.expectedTemplateUpdatedAt
                ),
                result.value
            )
        }
        return result
    }


    async resolveProcurementSourceSelection(input: {
        mode: RHProcurementSourceSelectionMode
        sourceId: string
        companyId: string
        expectedTemplateUpdatedAt?: string
    }): Promise<RHProcurementTemplateResult<RHProcurementSourceSelectionResult>> {
        if (input.mode === "previousProcurement") {
            return {
                success: YES,
                value: {
                    kind: "previousProcurement",
                    source: await this.retrieveProcurementCopySource(
                        input.sourceId,
                        input.companyId
                    )
                }
            }
        }
        const result = await this.retrieveProcurementTemplateForUse(
            input.companyId,
            input.sourceId,
            input.expectedTemplateUpdatedAt
        )
        if (!result.success) {
            // A picker row can become archived, deleted or otherwise unusable
            // between list load and selection. Ensure the dialog's canonical
            // reload does not immediately receive that stale cached row again.
            this._invalidateCopyProcurementCaches([input.sourceId])
            return result
        }
        return {
            success: YES,
            value: {
                kind: "template",
                source: result.value.source,
                templateId: result.value.templateId,
                companyId: result.value.companyId,
                templateLocks: result.value.templateLocks,
                expectedTemplateUpdatedAt: result.value.expectedTemplateUpdatedAt,
                capabilities: result.value.capabilities
            }
        }
    }


    async resolveProcurementTemplateLink(
        templateId: string
    ): Promise<RHProcurementTemplateResult<RHProcurementTemplateLinkValue>> {
        const response = await SocketClient.ResolveProcurementTemplateLink(
            { templateId },
            CBSocketClient.completionPolicy.first
        )
        return response.result
    }


    /** Candidate checklist for "Loo täpsustav hange". Not cached — offer prices can change between calls. */
    async retrieveReconciliationCandidatesForPart(
        procurementId: string,
        partId: string
    ): Promise<RHReconciliationCandidateCompany[]> {
        const response = await SocketClient.RetrieveReconciliationCandidatesForPart(
            { procurementId, partId },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result) {
            const fallback = "Täpsustava hanke kandidaatide laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        return response.result.candidates
    }


    /** Submitting the "Loo täpsustav hange" dialog — creates, publishes, registers tenderers, and broadcasts, all atomically. */
    async createAndPublishReconciliationProcurement(input: {
        procurementId: string
        partId: string
        invitedCompanyIds: string[]
        deadlineDate: Date
        broadcastMessage: string
    }): Promise<RHProcurement> {
        const response = await SocketClient.CreateAndPublishReconciliationProcurement(input)
        if (!response.result?.accepted) {
            const fallback = "Täpsustava hanke loomine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._notifySubscribers([input.procurementId, response.result.procurement._id + ""])
        return response.result.procurement
    }


    async exportProcurementExcel(procurementId: string): Promise<RHProcurementExcelExport> {
        const response = await SocketClient.ExportProcurementExcelForCurrentProcurerUser(
            { procurementId },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result) {
            const fallback = "Avamisprotokolli koostamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        return response.result
    }


    async shareOpeningProtocolForProcurement(procurementId: string): Promise<void> {
        const response = await SocketClient.ShareProcurementOpeningProtocol({ procurementId })
        if (!response.result?.sharedAt) {
            const fallback = "Avamise protokolli jagamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidatePublishedProcurements([procurementId])
    }


    async retrieveSharedOpeningProtocolForProcurement(procurementId: string): Promise<RHProcurementExcelExport> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (!offererCompanyId) {
            throw new Error("Vali ettevõte, kelle pakkumuse protokolli soovid vaadata.")
        }
        const response = await SocketClient.RetrieveSharedProcurementOpeningProtocol(
            { procurementId, offererCompanyId },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result) {
            const fallback = "Avamise protokolli laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        return response.result
    }


    async sendProcurementShareEmail(
        procurementId: string,
        recipients: string[],
        shareLink: string
    ): Promise<void> {
        const response = await SocketClient.SendProcurementShareEmail(
            { procurementId, recipients, shareLink },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result?.success) {
            const fallback = "Hanke lingi saatmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
    }

    async inviteCompaniesToProcurementWithInput(
        input: Omit<RHInviteCompaniesToProcurementInput, "companyId">
    ): Promise<RHProcurementInvitationResult> {
        const companyId = this._core.companyAffiliationStore.activeCompanyId
        if (!companyId) {
            throw new Error("Vali hankija ettevõte.")
        }
        const response = await SocketClient.InviteCompaniesToProcurement(
            { ...input, companyId }, CBSocketClient.completionPolicy.first
        )
        if (!response.result?.procurement) {
            const fallback = "Ettevõtete kutsumine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        if (companyId === this._core.companyAffiliationStore.activeCompanyId) {
            response.result.procurement = this._rememberCompleteProcurement(input.procurementId, response.result.procurement)
            this._clearOwnProcurementsCaches()
            this._clearPublishedProcurementsCaches()
            this._invalidateCopyProcurementCaches([input.procurementId])
            this._notifySubscribers([input.procurementId])
        }
        return response.result
    }


    /**
     * Fetches full procurement details for the details view — works for both
     * procurers (owner) and offerers.
     *
     * If the server returns files as bare ObjectId objects (no `name` field),
     * `RetrieveFilesMetadata` is called to fill them in. That enrichment is
     * best-effort: anonymous visitors may not have metadata access, and a
     * metadata failure must not prevent the public procurement itself from
     * being displayed.
     *
     * Returns a clean typed result — never the raw socket response wrapper.
     */
    async retrieveProcurementInformation(id: string): Promise<RHProcurementInformationResult> {
        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveProcurementInformation(
            { id, offererCompanyId },
            CBSocketClient.completionPolicy.first
        )
        const result = response.result
        if (!result?.procurement) {
            const fallback = "Hanke andmete laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        const procurement = result.procurement as (RHProcurement & RHProcurementPublic & RHProcurementClientExtras)
        const engagementSummary =
            result.engagementSummary ?? procurement?.engagementSummary
        if (procurement && engagementSummary) {
            procurement.engagementSummary = engagementSummary
        }

        // Hydrate files if any arrived as bare references (no name field).
        if (procurement?.files && procurement.files.length > 0) {
            const files: MongooseReference<RHFilePublic>[] = procurement.files
            const bareIds: string[] = []
            for (const file of files) {
                const bareId = referenceId(file)
                if (!populatedValueOfReference(file)?.name && bareId) {
                    bareIds.push(bareId)
                }
            }

            if (bareIds.length > 0) {
                try {
                    const metadata = (await SocketClient.RetrieveFilesMetadata(
                        { fileIds: bareIds, companyId: offererCompanyId },
                        CBSocketClient.completionPolicy.first
                    )).result

                    const metadataById = new Map<string, RHFileMetadata>(metadata.map((entry): [string, RHFileMetadata] => [entry.id + "", entry]))

                    const hydratedFiles = files.map(file => {
                        if (populatedValueOfReference(file)?.name) {
                            return file
                        }
                        const fileId = referenceId(file) ?? ""
                        const hydrated = metadataById.get(fileId)
                        if (hydrated) {
                            return populatedReferenceOf<RHFilePublic>({
                                _id: fileId,
                                name: hydrated.name,
                                type: hydrated.type,
                                size: hydrated.size,
                                wasCompressed: false,
                                createdAt: hydrated.uploadedAt,
                                uploaderName: hydrated.uploaderName,
                                downloadCount: hydrated.downloadCount
                            })
                        }
                        return file
                    })
                    procurement.files = hydratedFiles as typeof procurement.files
                }
                catch (error) {
                    procurement.files = files.filter(file => !!populatedValueOfReference(file)?.name) as typeof procurement.files
                    console.warn("RHProcurementStore: optional file metadata hydration failed", error)
                }
            }
        }

        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.retrieveProcurementInformation(id)
        }

        const rememberedProcurement = this._rememberCompleteProcurement(id, procurement)
        this._feedNotificationCounts([rememberedProcurement])

        return {
            procurement: rememberedProcurement,
            isCurrentUserOnProcuringSide: result.isCurrentUserOnProcuringSide,
            openingProtocolSharing: result.openingProtocolSharing,
            userOffers: result.userOffers ?? [],
            offerCapabilities: result.offerCapabilities,
            offerCreationCapabilities: result.offerCreationCapabilities,
            canWithdrawTendererRegistration:
                "canWithdrawTendererRegistration" in result && result.canWithdrawTendererRegistration === YES,
            unseenQANotificationCount: result.unseenQANotificationCount ?? 0,
            unseenOfferNotificationCount: result.unseenOfferNotificationCount ?? 0,
            unseenProcurementNotificationCount:
                result.unseenProcurementNotificationCount ?? 0,
            notificationSnapshotAt: result.notificationSnapshotAt ?? "",
            requiresProcurerAttention: result.requiresProcurerAttention === YES,
            requiresProcurerQAAttention: result.requiresProcurerQAAttention === YES,
            capabilities: result.capabilities,
            engagementSummary,
            offerCount: result.offerCount ?? 0,
            activePartOfferCount: result.activePartOfferCount ?? 0,
            lifecycleState: result.lifecycleState,
            isClosedWithoutContractInReconciliationChain: result.isClosedWithoutContractInReconciliationChain === YES,
            procurerInformativeStatus: result.procurerInformativeStatus,
            offererInformativeStatus: result.offererInformativeStatus,
            offererSubmissionOutcome: result.offererSubmissionOutcome,
            notificationStateGeneration
        }
    }


    async retrieveProcurementCapabilities(procurementId: string): Promise<RHProcurementCapabilities> {
        return (await SocketClient.RetrieveProcurementCapabilities(
            { procurementId },
            CBSocketClient.completionPolicy.first
        )).result
    }


    async retrieveRegistryProcurementForReferenceNumber(
        registryReferenceNumber: string
    ): Promise<RHRegistryProcurement | null> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveRegistryProcurementForReferenceNumber(
            { registryReferenceNumber, companyId },
            CBSocketClient.completionPolicy.first
        )
        return response.result.procurement
    }


    async retrieveInvitedCompaniesForCPVCode(cpvCode: string): Promise<InvitedCompany[]> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveInvitedCompaniesForCPVCode(
            { cpvCode, companyId },
            CBSocketClient.completionPolicy.first
        )
        return response.result ?? []
    }


    async retrieveInvitedCompanyFinancialsAndTrackRecordForCPVCodes(
        cpvCodes: string[],
        companies: InvitedCompany[]
    ): Promise<InvitedCompany[]> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveInvitedCompanyFinancialsAndTrackRecordForCPVCodes(
            { cpvCodes, companies, companyId },
            CBSocketClient.completionPolicy.first
        )
        return response.result ?? []
    }


    async retrieveProcurementTeam(procurementId: string): Promise<RHProcurementTeam> {
        const response = await SocketClient.RetrieveProcurementTeam(
            { procurementId },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result?.success) {
            throw new Error("retrieveProcurementTeam: server rejected the request")
        }
        return response.result.team
    }


    async updateProcurementTeam(
        procurementId: string,
        team: {
            authorizedProcuringUserIds: string[]
            observerProcuringUserIds: string[]
            evaluatorProcuringUserIds: string[]
        }
    ): Promise<RHProcurementTeam> {
        const response = await SocketClient.UpdateProcurementTeam({
            procurementId,
            authorizedProcuringUserIds: team.authorizedProcuringUserIds,
            observerProcuringUserIds: team.observerProcuringUserIds,
            evaluatorProcuringUserIds: team.evaluatorProcuringUserIds
        })
        if (!response.result?.success) {
            const fallback = "Meeskonna uuendamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.team
    }


    async changeProcurementResponsibleUser(
        procurementId: string,
        responsibleUserId: string
    ): Promise<RHProcurementTeam> {
        const response = await SocketClient.ChangeProcurementResponsibleUser({
            procurementId,
            responsibleUserId
        })
        if (!response.result?.success) {
            const fallback = "Hanke vastutava isiku muutmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.team
    }


    async addCurrentUserToProcurementTeamForRole(
        procurementId: string,
        role: Exclude<RHProcurementTeamRole, "responsible">
    ): Promise<RHProcurementTeam> {
        const response = await SocketClient.AddCurrentUserToProcurementTeam({
            procurementId,
            role
        })
        if (!response.result?.success) {
            const fallback = "Hankemeeskonnaga liitumine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.team
    }


    async confirmProcurementConflictOfInterest(procurementId: string): Promise<RHProcurementCapabilities> {
        const response = await SocketClient.ConfirmProcurementConflictOfInterest({ procurementId })
        if (!response.result?.success) {
            throw new Error("confirmProcurementConflictOfInterest: server rejected the request")
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.capabilities
    }


    /**
     * Read-only: the calling user's status from the cross-company conflict
     * engine (ConflictOfInterestService), distinct from the personal
     * confirmProcurementConflictOfInterest self-attestation above.
     */
    async retrieveConflictOfInterestStatus(procurementId: string): Promise<RHConflictOfInterestResult> {
        const response = await SocketClient.RetrieveConflictOfInterestStatusForProcurement({ procurementId })
        if (!response.result?.result) {
            throw new Error("retrieveConflictOfInterestStatus: server rejected the request")
        }
        return response.result.result
    }


    async submitConflictOfInterestDisclosure(
        procurementId: string,
        reason: string
    ): Promise<RHConflictOfInterestResult> {
        const response = await SocketClient.SubmitConflictOfInterestDisclosure({ procurementId, reason })
        if (!response.result?.result) {
            throw new Error("submitConflictOfInterestDisclosure: server rejected the request")
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.result
    }


    async requestConflictOfInterestRecusal(
        procurementId: string,
        fromSide: "procurement" | "offer",
        offerId?: string
    ): Promise<RHConflictOfInterestResult> {
        const response = await SocketClient.RequestConflictOfInterestRecusal({ procurementId, fromSide, offerId })
        if (!response.result?.result) {
            throw new Error("requestConflictOfInterestRecusal: server rejected the request")
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.result
    }


    async approveConflictOfInterestException(
        procurementId: string,
        userId: string,
        reasonCodes: RHConflictOfInterestReasonCode[],
        reason: string
    ): Promise<RHConflictOfInterestResult> {
        const response = await SocketClient.ApproveConflictOfInterestException({
            procurementId, userId, reasonCodes, reason
        })
        if (!response.result?.result) {
            throw new Error("approveConflictOfInterestException: server rejected the request")
        }
        this._invalidateProcurementTeamCaches(procurementId)
        return response.result.result
    }


    /**
     * Administrator-only discovery step for approveConflictOfInterestException:
     * every currently-blocked user/procurement pair for a company.
     */
    async retrieveUnresolvedConflictsForCompany(companyId: string): Promise<RHUnresolvedConflictEntry[]> {
        const response = await SocketClient.RetrieveUnresolvedConflictsForCompany({ companyId })
        return response.result?.conflicts ?? []
    }


    /**
     * Historical counterpart to retrieveUnresolvedConflictsForCompany: every
     * conflict for this company already resolved by an approved exception or a
     * recusal.
     */
    async retrieveResolvedConflictsForCompany(companyId: string): Promise<RHResolvedConflictEntry[]> {
        const response = await SocketClient.RetrieveResolvedConflictsForCompany({ companyId })
        return response.result?.conflicts ?? []
    }


    async retrieveTendererParticipation(
        procurementId: string
    ): Promise<RHProcurementTendererParticipation | null> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveTendererParticipation(
            { procurementId, offererCompanyId },
            CBSocketClient.completionPolicy.first
        )
        if (!response.result?.success) {
            throw new Error("retrieveTendererParticipation: server rejected the request")
        }
        return response.result.participation
    }


    async updateTendererParticipationAttentionUsers(
        procurementId: string,
        attentionUserIds: string[],
        observerUserIds: string[]
    ): Promise<RHProcurementTendererParticipation> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.UpdateTendererParticipationAttentionUsers({
            procurementId,
            attentionUserIds,
            observerUserIds,
            offererCompanyId
        })
        if (!response.result?.success) {
            const fallback = "Pakkuja meeskonna uuendamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidatePublishedProcurements([procurementId])
        return response.result.participation
    }


    async addCurrentUserToTendererParticipation(procurementId: string): Promise<RHProcurementTendererParticipation> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.AddCurrentUserToTendererParticipation({ procurementId, offererCompanyId })
        if (!response.result?.success) {
            const fallback = "Pakkuja meeskonda volitatud isikuna lisamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidatePublishedProcurements([procurementId])
        return response.result.participation
    }


    /**
     * Synchronous cache-only lookup. Returns the procurement if it has been
     * previously fetched and is in memory, otherwise undefined.
     *
     * Use this in cell descriptors after _reloadData() has pre-warmed the
     * cache via getProcurement() for all relevant IDs. Never use this as a
     * substitute for getProcurement() in async affiliations.
     */
    cachedProcurement(id: string): RHProcurement | undefined {
        return this._procurementById.get(id)
    }


    // =========================================================================
    // Write API — mutate → update cache → notify subscribers
    // =========================================================================

    async saveProcurementTemplate(
        input: Parameters<typeof SocketClient.AddOrModifyProcurementTemplate>[0]
    ): Promise<RHProcurementTemplateResult<RHProcurementTemplateMutationValue>> {
        const result = (await SocketClient.AddOrModifyProcurementTemplate(input)).result
        if (result.success) {
            const templateId = result.value.template._id!.toString()
            this._procurementById.set(templateId, result.value.template)
            this._afterTemplateMutation(input.companyId, templateId)
        }
        return result
    }


    async archiveProcurementTemplate(input: {
        companyId: string
        templateId: string
        expectedTemplateUpdatedAt: string
    }): Promise<RHProcurementTemplateResult<RHProcurementTemplateMutationValue>> {
        const result = (await SocketClient.ArchiveProcurementTemplate(input)).result
        if (result.success) {
            this._procurementById.set(input.templateId, result.value.template)
            this._afterTemplateMutation(input.companyId, input.templateId)
        }
        return result
    }


    async restoreProcurementTemplate(input: {
        companyId: string
        templateId: string
        expectedTemplateUpdatedAt: string
    }): Promise<RHProcurementTemplateResult<RHProcurementTemplateMutationValue>> {
        const result = (await SocketClient.RestoreProcurementTemplate(input)).result
        if (result.success) {
            this._procurementById.set(input.templateId, result.value.template)
            this._afterTemplateMutation(input.companyId, input.templateId)
        }
        return result
    }


    async deleteUnusedProcurementTemplate(input: {
        companyId: string
        templateId: string
        expectedTemplateUpdatedAt: string
    }): Promise<RHProcurementTemplateResult<{ templateId: string }>> {
        const result = (await SocketClient.DeleteUnusedProcurementTemplate(input)).result
        if (result.success) {
            this._procurementById.delete(input.templateId)
            this._afterTemplateMutation(input.companyId, input.templateId)
        }
        return result
    }


    async exportProcurementTemplateUsageXLSX(input: {
        companyId: string
        templateId: string
        procurementId?: string
        contractingAuthorityType?: RHContractingAuthorityType
        referenceDate?: string
    }): Promise<RHProcurementTemplateResult<RHProcurementTemplateUsageXLSXResult>> {
        return (await SocketClient.ExportProcurementTemplateUsageXLSX(
            input,
            CBSocketClient.completionPolicy.first
        )).result
    }


    async resolveTemplateInvitationImportRows(input: {
        companyId: string
        rows: RHProcurementTemplateInvitationImportRow[]
    }): Promise<RHProcurementTemplateResult<RHProcurementTemplateInvitationImportResult>> {
        return (await SocketClient.ResolveTemplateInvitationImportRows(
            input,
            CBSocketClient.completionPolicy.first
        )).result
    }


    async saveInvitedCompanyCustomizationForCPVCode(
        cpvCode: string,
        companies: InvitedCompany[]
    ): Promise<void> {
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.SaveInvitedCompaniesForCPVCode({
            cpvCode,
            companies,
            companyId
        })
        if (!response.result?.success) {
            throw new Error("Ettevõtete valiku salvestamine ebaõnnestus.")
        }
    }


    async saveOrModifyProcurement(
        data: Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0]
    ): Promise<RHProcurement> {
        const result = await this.saveOrModifyProcurementResult(data)
        if (!result?.accepted || !result.procurement) {
            throw new Error("Hanke salvestamine ebaõnnestus.")
        }
        return result.procurement
    }


    async saveOrModifyProcurementResult(
        data: Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0]
    ): Promise<RHAddOrModifyNewProcurementResult> {
        const response = await SocketClient.AddOrModifyNewProcurement(data)
        if (!response.result) {
            const fallback = "Hanke salvestamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        const result = response.result
        if (!result.accepted || !result.procurement) {
            return result
        }
        const id = result.procurement._id!.toString()
        result.procurement = this._rememberCompleteProcurement(id, result.procurement)
        this._clearOwnProcurementsCaches()
        this._invalidateCopyProcurementCaches([id])
        this._notifySubscribers([id])
        return result
    }


    async publishProcurementResultForProcurementId(
        procurementId: string,
        publicationVisibility: RHProcurementPublicationVisibility = "public"
    ): Promise<RHPublishProcurementResult> {
        const result = (await SocketClient.PublishProcurement({
            _id: procurementId,
            publicationVisibility,
            didPublishingUserConfirmNoConflictOfInterest: YES
        })).result
        if (!result?.accepted || !result.procurement) {
            return result
        }
        result.procurement = this._rememberCompleteProcurement(procurementId, result.procurement)
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches([procurementId])
        // Publishing changes which procurements are visible to offerers,
        // so any cached outgoing offer list that embeds procurement data
        // may be stale. Invalidate so the next read is fresh.
        this._core.offerStore.invalidateOutgoingOffers()
        this._notifySubscribers([procurementId])
        return result
    }


    async previewPublishedProcurementAmendment(
        input: RHAmendPublishedProcurementInput
    ): Promise<RHProcurementAmendmentPreview> {
        const message = this._core.messageByAddingActiveCompanyId({ ...input })
        const response = await SocketClient.PreviewPublishedProcurementAmendment(
            message,
            CBSocketClient.completionPolicy.first
        )
        const fallback = "Hanke muudatuse eelvaate koostamine ebaõnnestus."
        const errorMessage = RHProcurementStore._userFacingAmendmentErrorMessageForServerMessage(
            this._core.errorMessageFromResponse(response, fallback)
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result) {
            throw new Error(fallback)
        }
        return response.result
    }


    async amendPublishedProcurement(
        input: RHAmendPublishedProcurementInput
    ): Promise<RHProcurement> {
        const message = this._core.messageByAddingActiveCompanyId({ ...input })
        const response = await SocketClient.AmendPublishedProcurement(
            message,
            CBSocketClient.completionPolicy.first
        )
        const fallback = "Hanke muudatuse salvestamine ebaõnnestus."
        const errorMessage = RHProcurementStore._userFacingAmendmentErrorMessageForServerMessage(
            this._core.errorMessageFromResponse(response, fallback)
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.procurement) {
            throw new Error(fallback)
        }

        const id = response.result.procurement._id!.toString()
        const procurement = this._rememberCompleteProcurement(id, response.result.procurement)
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches([id])
        this._core.offerStore.invalidateIncomingOffers()
        this._core.offerStore.invalidateOutgoingOffers()
        this._notifySubscribers([id])
        return procurement
    }


    rememberDraftPreviewState(
        id: string,
        state: {
            publicationVisibility: RHProcurementPublicationVisibility
            invitedCompanies?: InvitedCompany[]
        }
    ) {
        const procurement = this._procurementById.get(id)
        if (!procurement) {
            return
        }

        procurement.publicationVisibility = state.publicationVisibility
        if ("invitedCompanies" in state) {
            procurement.invitedCompanies = state.invitedCompanies
        }
        this._notifySubscribers([id])
    }


    async cancelDraftProcurementWithId(id: string): Promise<{
        procurement: RHProcurement
        isCancellationConfirmedByRefresh: boolean
    }> {
        let procurement: RHProcurement
        let isCancellationConfirmedByRefresh = NO
        try {
            const response = await SocketClient.CancelDraftProcurement({ _id: id })
            if (!response.result?.success || !response.result.procurement) {
                const fallback = "Hanke koostamise tühistamine ebaõnnestus."
                throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
            }
            procurement = response.result.procurement
        }
        catch (error) {
            // A stale confirmation may arrive after another request has cancelled the draft.
            const currentProcurement = await this.refreshProcurement(id).catch(() => undefined)
            if (currentProcurement?.status !== "draft-cancelled") {
                throw error
            }
            procurement = currentProcurement
            isCancellationConfirmedByRefresh = YES
        }
        procurement = this._rememberCompleteProcurement(id, procurement)
        this._clearOwnProcurementsCaches()
        this._invalidateCopyProcurementCaches([id])
        this._notifySubscribers([id])
        return { procurement, isCancellationConfirmedByRefresh }
    }


    /**
     * Registers the current user's company as a tenderer for a published
     * procurement. Invalidates the published-procurements caches and notifies
     * subscribers so the "Avaldatud hanked" table immediately shows the
     * registered state (row tint + "Hankele registreeritud") without a refresh.
     * Returns the updated public procurement on success, throws on failure.
     */
    async registerAsTendererForProcurement(procurementId: string): Promise<RHProcurementPublic> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RegisterAsTendererForProcurement(
            { procurementId, offererCompanyId }
        )
        const result = response.result
        if (!result?.accepted || !result.procurement) {
            const serverMessage = this._core.errorMessageFromResponse(response)
            let userFacingMessage = "Hankele registreerumine ebaõnnestus."
            if (
                serverMessage ===
                "The procuring company cannot register as a tenderer for its own procurement."
            ) {
                userFacingMessage = "Endaga seotud ettevõtte hankele ei saa registreeruda"
            }
            throw new Error(userFacingMessage)
        }
        this._clearPublishedProcurementsCaches()
        this._procurementById.delete(procurementId)
        this._notifySubscribers([procurementId])
        return result.procurement
    }


    async withdrawTendererRegistrationForProcurement(procurementId: string): Promise<void> {
        const offererCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.WithdrawTendererRegistrationForProcurement({
            procurementId,
            offererCompanyId
        })
        if (!response.result?.success) {
            const fallback = "Hankele registreerimise tühistamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this.invalidateCompanyScopedCaches([procurementId])
    }


    // =========================================================================
    // Cache invalidation — called by socket push listeners
    // =========================================================================

    private _invalidateOwnProcurements(changedIds: string[]) {
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches(changedIds)
        changedIds.forEach(id => this._procurementById.delete(id))
        this._notifySubscribers(changedIds)
    }


    private _invalidatePublishedProcurements(changedIds: string[]) {
        this._clearPublishedProcurementsCaches()
        this._cpvSummationRequestsByKey.clear()
        this._cpvSummationGeneration += 1
        this._invalidateCopyProcurementCaches(changedIds)
        changedIds.forEach(id => this._procurementById.delete(id))
        this._notifySubscribers(changedIds)
    }


    _invalidateCopyProcurementCaches(changedIds?: string[]) {
        this._procurementSourceListsByCompanyAndMode.clear()
        if (!changedIds || changedIds.length === 0) {
            this._copySourceByCompanyAndId.clear()
            this._templateUseByCompanyTemplateAndVersion.clear()
            return
        }
        for (const id of changedIds) {
            for (const key of this._copySourceByCompanyAndId.keys()) {
                if (key.endsWith(`:${id}`)) {
                    this._copySourceByCompanyAndId.delete(key)
                }
            }
            for (const key of this._templateUseByCompanyTemplateAndVersion.keys()) {
                if (key.includes(`:${id}:`)) {
                    this._templateUseByCompanyTemplateAndVersion.delete(key)
                }
            }
        }
    }


    _afterTemplateMutation(companyId: string, templateId: string) {
        this._clearOwnProcurementsCaches()
        this._invalidateCopyProcurementCaches([templateId])
        this._notifySubscribers([templateId])
    }


    private _invalidateProcurementTeamCaches(procurementId: string) {
        this._procurementById.delete(procurementId)
        this._clearOwnProcurementsCaches()
        this._notifySubscribers([procurementId])
    }


    /**
     * Public cache invalidation for the published-procurements lists. Used by
     * flows that change per-row data embedded in the published response WITHOUT
     * a server push to this client — e.g. after the offerer reads the answered
     * questions of a procurement (unseenAnsweredQuestionCount drops to 0), so
     * the "Avaldatud hanked" row dot clears on the next read.
     */
    invalidatePublishedProcurements(changedIds: string[] = []) {
        this._invalidatePublishedProcurements(changedIds)
    }


    /**
     * Public cache invalidation for every company-scoped procurement list and
     * detail cache. Called whenever the active company affiliation changes so a
     * stale list from a previously active company is never shown as if it
     * belonged to the newly active one.
     */
    invalidateCompanyScopedCaches(changedIds: string[] = []) {
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches(changedIds)
        changedIds.forEach(id => this._procurementById.delete(id))
        this._notifySubscribers(changedIds)
    }


    /**
     * Invalidates every procurement cache whose rows embed notification state.
     *
     * A notification acknowledgement can originate from another view or from
     * another active session of the same user. In that case the cached own and
     * published lists still carry their old `unseenNotificationCount`,
     * `unseenAnsweredQuestionCount`, and `requiresOffererAttention` fields even
     * though RHNotificationStore already has the fresh per-procurement count.
     * Clearing both list caches before notification-store subscribers run makes
     * their next read fetch the same acknowledged state from the server. The
     * by-ID detail cache is deliberately retained: its endpoint does not carry
     * notification metadata and incoming-offer views use it for row details.
     *
     * This method intentionally does not notify procurement-store subscribers:
     * RHNotificationStore owns the acknowledgement transaction and sends one
     * notification with the affected IDs after all related caches are stale.
     */
    invalidateNotificationStateCaches(changedIds: string[]) {
        if (changedIds.length === 0) {
            return
        }
        this._ownProcurementsByStatusKey.clear()
        this._clearPublishedProcurementsCaches()
    }


    /**
     * Resolves the acting company id for explicit-company socket calls, or
     * `undefined` when multi-company support is disabled or no active company
     * affiliation is known yet — callers then fall back to the legacy implicit
     * (single-company) socket target.
     */
    async _activeCompanyIdForExplicitCompanyCalls(): Promise<string | undefined> {
        if (RHFeatureFlags.multiCompanyAffiliationIsEnabled !== YES) {
            return undefined
        }
        return this._core.companyAffiliationStore.activeCompanyIdAfterLoadingAffiliations()
    }


    // =========================================================================
    // Notification count helpers
    // =========================================================================

    /**
     * Extracts the `unseenNotificationCount` field from company-scoped items
     * (cast through `any` because the field is not in the generated model types)
     * and forwards all non-zero — and zero — values to the notification store.
     * Unscoped and all-company lists keep these counts embedded in their rows.
     */
    private _feedNotificationCounts(items: (Pick<RHProcurement, "_id"> & RHProcurementClientExtras)[]) {
        const counts: Record<string, number> = {}
        for (const item of items) {
            const id = item._id?.toString()
            if (!id || !Object.prototype.hasOwnProperty.call(item, "unseenNotificationCount")) {
                continue
            }
            counts[id] = item.unseenNotificationCount ?? 0
        }
        this._core.notificationStore.setCounts(counts)
    }


    // =========================================================================
    // Socket push listeners
    // =========================================================================

    private _registerSocketListeners() {
        const socketClient = this._core.socketClient

        socketClient.addTargetForMessagesForKey(
            "OwnProcurementsDidChange",
            (payload: RHProcurementChangePayload) => {
                this._invalidateOwnProcurements(payload.changedIds ?? [])
            }
        )

        socketClient.addTargetForMessagesForKey(
            "PublishedProcurementsDidChange",
            (payload: RHProcurementChangePayload) => {
                this._invalidatePublishedProcurements(payload.changedIds ?? [])
            }
        )

        // Push from server when a procurer changes the deadline of a
        // procurement that the current user is registered as a tenderer for.
        // The store invalidates caches and lets RHCore broadcast a view-tree
        // event so the UI can show the notification dialog.
        socketClient.addTargetForMessagesForKey(
            "ProcurementDeadlineChanged",
            (payload: RHProcurementDeadlineChangedPayload) => {
                this._invalidatePublishedProcurements([payload.procurementId])
                this._onDeadlineChanged?.(payload)
            }
        )

        socketClient.addTargetForMessagesForKey(
            "ProcurementAmended",
            (payload: RHProcurementAmendedPayload) => {
                this._procurementById.delete(payload.procurementId)
                this._clearOwnProcurementsCaches()
                this._clearPublishedProcurementsCaches()
                this._invalidateCopyProcurementCaches([payload.procurementId])
                this._notifySubscribers([payload.procurementId])
                this._onProcurementAmended?.(payload)
            }
        )
    }


    // =========================================================================
    // Static utilities
    // =========================================================================

    private static _statusCacheKey(statuses: readonly string[]): string {
        return [...statuses].sort().join("|")
    }

    static _companyModeCacheKey(
        companyId: string | undefined,
        mode: string
    ): string {
        return `${companyId ?? "implicit"}:${mode}`
    }

    static _templateUseCacheKey(
        companyId: string,
        templateId: string,
        expectedTemplateUpdatedAt: string
    ): string {
        return `${companyId}:${templateId}:${expectedTemplateUpdatedAt}`
    }

    // Mirrors the server-side gate in ProcurementsController._hasCompanyProcurementListAccess:
    // direct company roles get list access via procurer/administrator/auditor; a
    // relationship-derived (delegated) affiliation needs the child-management policy
    // specifically. Filtering here avoids wasted fan-out calls the server would
    // reject anyway — the server re-checks this itself regardless.
    static _affiliationHasOwnProcurementListAccess(affiliation: RHCompanyAffiliation): boolean {
        if (affiliation.source !== "relationship") {
            return affiliation.isProcurer === YES || affiliation.isAdministrator === YES || affiliation.isAuditor === YES
        }
        return affiliation.relationshipPolicy?.canManageProcurementsForChild === YES
    }

    /**
     * Flattens dashboard/published-list rows and their nested
     * `reconciliationRounds` (rounds no longer appear as their own top-level
     * rows — see ProcurementsController._nestReconciliationRoundRows) into a
     * single list, so per-procurement notification counts still reach a round
     * the same way they would have when it was a top-level row. Generic so
     * both the procurer's own dashboard rows and the published-procurements
     * table's rows can share it.
     */
    static _withReconciliationRoundsFlattened<RowType extends { reconciliationRounds?: RowType[] }>(
        rows: RowType[]
    ): RowType[] {
        return rows.flatMap(row => [
            row,
            ...RHProcurementStore._withReconciliationRoundsFlattened(row.reconciliationRounds ?? [])
        ])
    }

    static _ownOngoingProcurementSortValue(procurement: RHProcurerDashboardProcurement): string {
        const sortDate = procurement.publishedAt ?? procurement.createdAt
        if (!sortDate) {
            return ""
        }
        return new Date(sortDate).toISOString()
    }


    static _userFacingAmendmentErrorMessageForServerMessage(
        serverMessage?: string
    ): string | undefined {
        if (serverMessage === "Company affiliation required.") {
            return "Hanke muutmiseks vali ülemisest menüüst ettevõte."
        }
        return serverMessage
    }

    /**
     * Sums the `estimatedCost` of all parts in a procurement.
     * Returns `undefined` if no part has an estimated cost.
     * Use this anywhere the total estimated cost needs to be displayed —
     * `estimatedCost` does not exist as a top-level field on `RHProcurement`.
     */
    static totalEstimatedCost(procurement: { parts?: { estimatedCost?: number }[] } | undefined): number | undefined {
        const costs = (procurement?.parts ?? [])
            .map(part => part.estimatedCost)
            .filter((cost): cost is number => typeof cost === "number" && Number.isFinite(cost))
        if (costs.length === 0) {
            return undefined
        }
        return costs.reduce((sum, cost) => sum + cost, 0)
    }


    // =========================================================================
    // Deadline-changed callback — wired by RHCore
    // =========================================================================

    /**
     * Called whenever the server pushes a `ProcurementDeadlineChanged` message
     * to this client. RHCore sets this once during initialisation.
     */
    _onDeadlineChanged?: (payload: RHProcurementDeadlineChangedPayload) => void

    _onProcurementAmended?: (payload: RHProcurementAmendedPayload) => void


    // =========================================================================
    // Notification count — optimistic patch
    // =========================================================================

    /**
     * Zeroes the `unseenNotificationCount` field for a single procurement in
     * every in-memory cache that carries it, then notifies subscribers so that
     * any data view displaying the procurement (e.g. ProcurerMainViewController,
     * OffererMainViewController) immediately re-renders the badge as 0 — without
     * a round-trip to the server.
     *
     * Call this immediately after `MarkEventLogEntriesAsSeen` resolves for a
     * given procurement (or after any other action that clears per-procurement
     * notifications), so the main-view badge is cleared in sync with everywhere
     * else in the UI.
     */
    markProcurementNotificationsSeen(procurementId: string) {
        for (const dashboard of this._ownProcurementsByStatusKey.values()) {
            const own = dashboard.rows.find(p => p._id?.toString() === procurementId)
            if (own) {
                own.unseenNotificationCount = 0
            }
        }

        for (const procurements of this._ownProcurementsAcrossAllCompaniesByStatusKey.values()) {
            const own = procurements.find(p => p._id?.toString() === procurementId)
            if (own) {
                own.unseenNotificationCount = 0
            }
        }

        for (const procurements of this._publishedProcurementsByStatusKey.values()) {
            const published = procurements.find(p => p._id?.toString() === procurementId)
            if (published) {
                published.unseenNotificationCount = 0
            }
        }

        const byId = this._procurementById.get(procurementId)
        if (byId) {
            byId.unseenNotificationCount = 0
        }

        this._notifySubscribers([procurementId])
    }


    async reopenPartEvaluationForProcurement(
        procurementId: string,
        partId: string,
        reason: string,
        winningOfferWasWithdrawn: boolean,
        confirmsNoContract: boolean
    ): Promise<RHProcurement> {
        const response = await SocketClient.ReopenProcurementPartEvaluation({
            procurementId, partId, reason, winningOfferWasWithdrawn, confirmsNoContract
        })
        if (!response.result?.success || !response.result.procurement) {
            const fallback = "Otsuse muutmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this.invalidateCompanyScopedCaches([procurementId])
        this._core.offerStore.invalidateIncomingOffers()
        this._core.offerStore.invalidateOutgoingOffers([procurementId])
        this._core.contractStore.invalidateContractsForProcurement(procurementId)
        return response.result.procurement
    }

    async closeProcurementWithoutContract(procurementId: string, reason: string): Promise<RHProcurement> {
        const result = (await SocketClient.CloseProcurementWithoutContract({
            _id: procurementId,
            reason
        })).result
        if (!result.success || !result.procurement) {
            throw new Error("closeProcurementWithoutContract: server rejected the request")
        }
        const procurement: RHProcurement & RHProcurementClientExtras = result.procurement
        const id = procurement._id!.toString()
        const cachedEngagementSummary = this._procurementById.get(id)?.engagementSummary
        if (cachedEngagementSummary) {
            procurement.engagementSummary = cachedEngagementSummary
        }
        const rememberedProcurement = this._rememberCompleteProcurement(id, procurement)
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches([id])
        this._notifySubscribers([id])
        return rememberedProcurement
    }


    async closeProcurementPartWithoutContract(procurementId: string, partId: string, reason: string): Promise<RHProcurement> {
        const result = (await SocketClient.CloseProcurementPartWithoutContract({
            procurementId,
            partId,
            reason
        })).result
        if (!result.success || !result.procurement) {
            throw new Error("closeProcurementPartWithoutContract: server rejected the request")
        }
        const procurement: RHProcurement & RHProcurementClientExtras = result.procurement
        const id = procurement._id!.toString()
        const cachedEngagementSummary = this._procurementById.get(id)?.engagementSummary
        if (cachedEngagementSummary) {
            procurement.engagementSummary = cachedEngagementSummary
        }
        const rememberedProcurement = this._rememberCompleteProcurement(id, procurement)
        this._clearOwnProcurementsCaches()
        this._clearPublishedProcurementsCaches()
        this._invalidateCopyProcurementCaches([id])
        this._notifySubscribers([id])
        return rememberedProcurement
    }

}
